// The choices of a cast (qa-runs#366; docs/MRCS/feats.md §10b): a sheet spell clicked on the action bar or hot bar, by a
// caster whose sheet lists metamagic, stops here first. The choices, their costs and their refusals are the system's
// data, read by `applyMetamagic` against the spell's sheet row; the server checks the same again before it commits.
import { useEffect, useState } from 'react';
import { applyMetamagic, metamagicRefusal, type MetamagicAsk, type MetamagicCast, type MetamagicChoice, type MetamagicRules, type Spell } from '@dragons-table/vtt-shared';
import { fetchSheetSpells, type SheetSpellRow } from '../sheetSpells';
import { BTN } from '../shell/ui';

export interface MetaChosen { ask: MetamagicAsk; cast: MetamagicCast }

/** What the wire carries beside the cast: the choices, never what they do, which the server works out for itself. */
export const metaWire = (m: MetaChosen | undefined, secret: boolean) => (m ? { metamagic: m.ask.keys, ...(m.ask.raise !== undefined ? { raise: m.ask.raise } : {}), ...(secret ? { private: true } : {}) } : {});

export function MetamagicPicker({ spell, rules, known, campaignId, tokenId, sceneId, onCast, onClose }: {
  spell: Spell; rules: MetamagicRules; known: MetamagicChoice[]; campaignId: string; tokenId: string; sceneId: string | null;
  onCast: (m: MetaChosen | null) => void; onClose: () => void;
}) {
  const [row, setRow] = useState<SheetSpellRow | null | 'asking'>('asking');
  const [keys, setKeys] = useState<string[]>([]);
  const [raise, setRaise] = useState('');
  useEffect(() => {
    let live = true;
    void fetchSheetSpells(campaignId, tokenId, sceneId).then((a) => { if (live) setRow(a?.rows.find((r) => r.row === spell.row) ?? null); });
    return () => { live = false; };
  }, [campaignId, tokenId, sceneId, spell.row]);
  const caster = row && row !== 'asking' && row.level !== undefined && row.top !== undefined
    ? { level: row.level, top: row.top, spontaneous: row.spontaneous === true, casting: row.casting ?? null } : null;
  const ask: MetamagicAsk = { keys, ...(raise.trim() !== '' && Number.isInteger(Number(raise)) ? { raise: Number(raise) } : {}) };
  const got = caster && keys.length ? applyMetamagic(rules, known, spell, caster, ask) : null;
  const refused = got && 'refused' in got ? got.refused : null;
  const cast = got && !('refused' in got) ? got : null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs text-amber-200" role="group" aria-label={`Metamagic for ${spell.name}`} data-testid="metamagic-picker">
      <span>{spell.name}{caster ? ` (level ${caster.level})` : ''}:</span>
      {row === 'asking' && <span className="text-stone-400">Reading the sheet…</span>}
      {row !== 'asking' && !caster && <span className="text-stone-400">The sheet doesn't say this spell's level, so it can only be cast as it is.</span>}
      {caster && known.map((c) => {
        const no = metamagicRefusal(c, spell, caster);
        const on = keys.includes(c.key);
        return (
          <label key={c.key} className={`flex items-center gap-1 ${no ? 'text-stone-500' : ''}`} title={no ?? (c.note ?? c.feat)}>
            <input type="checkbox" aria-label={c.label} disabled={!!no} checked={on} onChange={() => setKeys(on ? keys.filter((k) => k !== c.key) : [...keys, c.key])} />
            {c.label}{c.slots === 'choose' ? '' : ` +${c.slots}`}
            {c.slots === 'choose' && on && <input aria-label={`${c.label} to level`} type="number" min={caster.level + 1} max={rules.highest} className="w-10 rounded bg-stone-800 px-1" value={raise} onChange={(e) => setRaise(e.target.value)} />}
          </label>
        );
      })}
      {cast && <span data-testid="metamagic-preview">Uses a level {cast.slot} slot.</span>}
      {refused && <span className="text-amber-300" data-testid="metamagic-refused">{refused}</span>}
      <button type="button" className={BTN} disabled={!!refused || row === 'asking'} onClick={() => onCast(cast ? { ask, cast } : null)}>Cast</button>
      <button type="button" className={BTN} onClick={onClose}>Cancel</button>
    </div>
  );
}
